import React, { useContext } from "react";
import { Link } from "react-router-dom";

import cx from "classnames";

import { showFeatureModal } from "~/billing";
import ThemeContext from "~/common/ThemeContext";
import { ReactComponent as MagnifyingGlassCircleDark } from "~/legacy-assets/images/icons/search-empty-dark.svg";
import { Button } from "~/ui";

import ViewportContext from "../common/ViewportContext";
import { ReactComponent as CreateNoteIllo } from "./assets/CreateNote-illo.svg";
import { ReactComponent as MagnifyingGlassCircle } from "./assets/magnifying-glass-circle.svg";
import { type Category, FixedCategory } from "./category";

import type { TemplateVisibility } from "~/__generated__/noteTemplateGqlSaveTemplateMutation.graphql";

import styles from "./css/emptyState.module.css";

export function EmptyState({
	heading,
	aBitNarrower,
	children,
	illo: Illo,
	illoClass,
	className,
}: React.PropsWithChildren<{
	heading: React.ReactNode;
	aBitNarrower?: boolean;
	illo: React.ComponentType<{ className?: string }>;
	illoClass?: string;
	className?: string;
}>) {
	return (
		<div className={cx(styles.emptyState, className, { [styles.narrower]: aBitNarrower })}>
			<Illo className={illoClass} />
			<p className={styles.heading}>{heading}</p>
			{children}
		</div>
	);
}

export const EmptyStateBody: React.FC<React.HTMLProps<HTMLParagraphElement>> = props => (
	<p className={styles.body} {...props} />
);

function BaseEmptyState({
	heading,
	children,
	aBitNarrower,
}: React.PropsWithChildren<{ heading: React.ReactNode; aBitNarrower?: boolean }>) {
	return (
		<EmptyState illo={CreateNoteIllo} illoClass={styles.createNoteIllo} heading={heading} aBitNarrower={aBitNarrower}>
			{children}
		</EmptyState>
	);
}

export function CategoryEmptyState({
	category,
	canCreateWorkspaceTemplate,
	hasAccessToWorkspaceTemplates,
	subscriptionProductName,
	createTemplate,
	inSection = false,
}: {
	category: Category;
	canCreateWorkspaceTemplate: boolean;
	hasAccessToWorkspaceTemplates: boolean;
	subscriptionProductName: string;
	createTemplate: (initialVisibility?: TemplateVisibility) => void;
	inSection?: boolean;
}) {
	const onCreateUserTemplate = React.useCallback(() => {
		createTemplate("USER");
	}, [createTemplate]);
	const onCreateWorkspaceTemplate = React.useCallback(() => {
		createTemplate("WORKSPACE");
	}, [createTemplate]);

	const { isMobile } = useContext(ViewportContext);
	const mobileTemplateMessage = (
		<p className={styles.heading}>Visit Fellow in a browser or on our desktop app to create a template.</p>
	);

	const showTemplatePaywall = React.useCallback(() => showFeatureModal("templates"), []);

	switch (category) {
		case FixedCategory.MY:
			return (
				<BaseEmptyState heading="You don&rsquo;t have any custom templates yet.">
					<EmptyStateBody>
						{isMobile ? (
							mobileTemplateMessage
						) : (
							<Button size="wide" variant="primary" onClick={onCreateUserTemplate} className={styles.button}>
								Create new template
							</Button>
						)}
					</EmptyStateBody>
				</BaseEmptyState>
			);
		case FixedCategory.WORKSPACE:
			return (
				<BaseEmptyState
					aBitNarrower={!hasAccessToWorkspaceTemplates}
					heading={
						hasAccessToWorkspaceTemplates ? (
							<>Your workspace doesn&rsquo;t have any custom templates yet.</>
						) : (
							<>
								<strong>Workspace templates</strong> is a feature in the Business plan, your account is on the{" "}
								{subscriptionProductName} plan.{" "}
								<a href="https://fellow.app/pricing/" target="_blank" rel="noopener">
									Learn more
								</a>
							</>
						)
					}
				>
					{hasAccessToWorkspaceTemplates ? (
						<EmptyStateBody>
							{canCreateWorkspaceTemplate
								? !inSection && (
										<>
											You can create Workspace templates from the{" "}
											<Link to="/templates-section/">Templates section</Link>.
										</>
									)
								: "Ask a workspace admin to create workspace templates."}
							{canCreateWorkspaceTemplate &&
								inSection &&
								(isMobile ? (
									mobileTemplateMessage
								) : (
									<Button size="wide" variant="primary" className={styles.button} onClick={onCreateWorkspaceTemplate}>
										Create new template
									</Button>
								))}
						</EmptyStateBody>
					) : (
						<EmptyStateBody>
							{isMobile ? (
								mobileTemplateMessage
							) : (
								<Button
									size="wide"
									variant="billing"
									className={styles.button}
									onClick={showTemplatePaywall}
									data-test-id="create-new-template-button-no-access"
								>
									Create new template
								</Button>
							)}
						</EmptyStateBody>
					)}
				</BaseEmptyState>
			);
		default:
			return (
				<BaseEmptyState heading="There aren't any templates in here yet.">
					{isMobile ? (
						mobileTemplateMessage
					) : (
						<Button size="wide" variant="primary" className={styles.button} onClick={onCreateUserTemplate}>
							Create new template
						</Button>
					)}
				</BaseEmptyState>
			);
	}
}

export function NoSearchResults({ query }: { query: string }) {
	const { isDarkMode } = useContext(ThemeContext);

	return (
		<EmptyState
			className={styles.searchEmptyState}
			illo={isDarkMode ? MagnifyingGlassCircleDark : MagnifyingGlassCircle}
			heading={
				<>
					We couldn&rsquo;t find any results for <strong>&ldquo;{query}&rdquo;</strong>.
				</>
			}
		>
			<EmptyStateBody>You may want to try using different keywords or checking for typos.</EmptyStateBody>
		</EmptyState>
	);
}
